/* Iter 194 — Completed Invoices bar for the document tools.
 * Collapsible strip shown above a tool's upload area listing invoices
 * marked Completed today (Order Register). Clicking "Use" hands the
 * invoice to the tool's normal generate handler (invoice override) so
 * the output is produced by the exact same code path as a file upload.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { CheckCircle2, ChevronDown, ChevronRight, RefreshCw, Search, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API, getAuthHeader } from "@/App";
import { useWorkingWindowLabel } from "@/hooks/useWorkingWindow";
import { HolidaysButton } from "./HolidaysButton";
import { DirectCreateBox, InTableBadge } from "./DirectCreate";
import { MissingFilesCard } from "./MissingFilesCard";
import { ReadyBadge, invalidateReady } from "./ReadyBadge";

// Iter 196 — soft chime when a NEW invoice_key appears in the bar.
// Persist the mute preference across reloads. Uses WebAudio (no asset).
const CHIME_MUTE_KEY = "completedInvoicesBar:muted";

// Iter 228 — label lookup for the used-badge tooltip.
const toolLabel = (t) => ({
  bulk_data: "Bulk Data",
  packing_list: "Packing List",
  pack_team: "Pack Team Print",
  bulk_label: "Bulk Label",
}[t] || t);
const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    // Two-note descending chime — pleasant, short (~0.35s total)
    const notes = [
      { f: 880, t: 0.00, d: 0.18 },   // A5
      { f: 659.25, t: 0.14, d: 0.22 } // E5
    ];
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(n.f, now + n.t);
      gain.gain.setValueAtTime(0.0001, now + n.t);
      gain.gain.exponentialRampToValueAtTime(0.18, now + n.t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + n.t + n.d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + n.t);
      osc.stop(now + n.t + n.d + 0.02);
    }
    setTimeout(() => ctx.close(), 500);
  } catch { /* silent on unsupported browsers */ }
};

export const CompletedInvoicesBar = ({ onUse, busy, hint, testId, tool, history }) => {
  const histLen = (history || []).length;
  useEffect(() => { if (histLen) invalidateReady(); }, [histLen]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [q, setQ] = useState("");
  // Iter 280 — click-to-filter from the Pending-Parcels strip.
  // When fired, scope the list to invoices that have NOT yet been
  // generated for this tool (uses_tool[tool] < 1), auto-open the bar,
  // and show a dismissable chip.
  const [pendingOnly, setPendingOnly] = useState(false);
  // Iter 283 — scope of the list. Default: today + yesterday (per ops
  // workflow — invoices completed yesterday still need doc work today).
  // 'today' and 'all' selectable. Persisted per browser.
  const ww = useWorkingWindowLabel();
  const SCOPE_OPTIONS = [
    { id: 'today', label: 'Today' },
    { id: 'today_yesterday', label: ww?.label || 'Today + Yesterday' },
    { id: 'all', label: 'All Time' },
  ];
  const [scope, setScope] = useState(() => {
    try { return localStorage.getItem('ci-scope') || 'today_yesterday'; }
    catch { return 'today_yesterday'; }
  });
  const setScopePersist = (v) => {
    setScope(v);
    try { localStorage.setItem('ci-scope', v); } catch { /* ignore */ }
    // Iter 290 — keep the Pending-Parcels strip on the same window.
    window.dispatchEvent(new CustomEvent('ci-scope-changed', { detail: v }));
  };
  const scopeLabel = SCOPE_OPTIONS.find(o => o.id === scope)?.label || scope;

  // Iter 290 — follow scope changes made from the Pending-Parcels strip.
  useEffect(() => {
    const onScope = (ev) => {
      if (ev?.detail && SCOPE_OPTIONS.some(o => o.id === ev.detail)) {
        setScope(ev.detail);
      }
    };
    window.addEventListener('ci-scope-changed', onScope);
    return () => window.removeEventListener('ci-scope-changed', onScope);
    // eslint-disable-next-line
  }, []);
  const [muted, setMuted] = useState(
    () => typeof window !== "undefined" &&
           localStorage.getItem(CHIME_MUTE_KEY) === "1");
  const knownKeysRef = useRef(null);   // null on first load ⇒ no chime

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/order-register/completed-today`,
        { headers: getAuthHeader(), params: { scope, tool: tool || "" } });
      const list = r.data.invoices || [];
      const keys = new Set(list.map(i => i.invoice_key));
      // Chime + toast only when a NEW invoice_key appears (skip first
      // load). The toast always shows — even when the chime is muted —
      // so the arrival stays visible.
      if (knownKeysRef.current) {
        const added = [...keys].filter(k => !knownKeysRef.current.has(k));
        if (added.length) {
          if (!muted) playChime();
          const names = added.slice(0, 3).join(", ");
          const extra = added.length > 3 ? ` +${added.length - 3} more` : "";
          toast.success(
            `New completed invoice${added.length > 1 ? "s" : ""}: ${names}${extra}`,
            { description: "Ready to use in the document tools",
              icon: <CheckCircle2 className="w-4 h-4 text-emerald-600" /> });
        }
      }
      knownKeysRef.current = keys;
      setInvoices(list);
    } catch { /* bar stays empty */ } finally { setLoading(false); }
  }, [muted, scope]);

  const toggleMute = () => {
    setMuted(m => {
      const next = !m;
      try { localStorage.setItem(CHIME_MUTE_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  useEffect(() => { fetchList(); }, [fetchList]);

  // Iter 195 — auto-refresh when an invoice is marked
  // Completed/In Progress in the Order Register, without a page reload.
  useEffect(() => {
    const handler = () => fetchList();
    window.addEventListener("order-register:invoice-changed", handler);
    // Also refresh when the tab regains focus (e.g. user switches from
    // Order Register in another tab and comes back to a doc tool).
    const onVis = () => { if (!document.hidden) fetchList(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("order-register:invoice-changed", handler);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchList]);

  const directList = invoices.filter(i => i.direct);
  const useList = invoices.filter(i => !i.direct);
  const matches = q.trim()
    ? useList.filter(i =>
        i.invoice_no.toLowerCase().includes(q.trim().toLowerCase()) ||
        (i.codes || []).join(" ").toLowerCase().includes(q.trim().toLowerCase()) ||
        (i.customer_name || "").toLowerCase().includes(q.trim().toLowerCase()))
    : useList;
  const filtered = pendingOnly
    ? matches.filter(i => Number(i.uses_tool?.[tool] || 0) === 0)
    : matches;

  // Listen for the Pending-strip click so we can scope + auto-open.
  useEffect(() => {
    const onPending = (ev) => {
      if (ev?.detail?.tool && ev.detail.tool !== tool) return;
      setPendingOnly(true);
      setOpen(true);
    };
    window.addEventListener("completed-invoices:pending-filter", onPending);
    return () => window.removeEventListener("completed-invoices:pending-filter", onPending);
  }, [tool]);

  return (
    <div className="space-y-2">
    <MissingFilesCard tool={tool} testId={testId} busy={busy} refreshKey={(history || []).length}
                      onUse={(m) => onUse(invoices.find(i => i.invoice_key === m.invoice_key) || m)} />
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60"
         data-testid={`completed-invoices-bar-${testId}`}>
      <div role="button" tabIndex={0} onClick={() => setOpen(o => !o)}
              onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen(o => !o); } }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer"
              data-testid={`completed-invoices-toggle-${testId}`}>
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
        <span className="text-xs font-semibold text-emerald-800">Completed Invoices ({scopeLabel})</span>
        <span className="text-[10px] font-bold text-emerald-700 bg-white border border-emerald-200 rounded-full px-1.5"
              data-testid={`completed-invoices-count-${testId}`}>
          {useList.length}
        </span>
        {/* Iter 283 — scope switcher, always visible so ops can widen the
            window without opening the bar first. Default Today + Yesterday. */}
        <span className="hidden sm:inline-flex items-center rounded-full bg-white border border-emerald-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid={`completed-invoices-scope-${testId}`}>
          {SCOPE_OPTIONS.map((o) => (
            <button key={o.id} type="button"
                    onClick={(e) => { e.stopPropagation(); setScopePersist(o.id); }}
                    data-testid={`completed-invoices-scope-${o.id}-${testId}`}
                    className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                      scope === o.id
                        ? "bg-emerald-600 text-white"
                        : "text-emerald-700 hover:bg-emerald-100"
                    }`}>
              {o.label}
            </button>
          ))}
        </span>
        <span className="hidden sm:inline-flex" onClick={(e) => e.stopPropagation()}><HolidaysButton testId={testId} /></span>
        {hint && (
          <span className="text-[10px] text-emerald-600 hidden md:inline truncate">— {hint}</span>
        )}
        <span onClick={(e) => { e.stopPropagation(); toggleMute(); }}
              className="ml-auto shrink-0 p-1 rounded hover:bg-emerald-100 text-emerald-600"
              role="button" tabIndex={0}
              title={muted ? "Chime muted — click to unmute" : "Chime on — click to mute"}
              data-testid={`completed-invoices-mute-${testId}`}>
          {muted
            ? <VolumeX className="w-3.5 h-3.5" />
            : <Volume2 className="w-3.5 h-3.5" />}
        </span>
        <span className="text-emerald-600 shrink-0">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </div>
      {open && (
        <div className="px-3 pb-3 space-y-2" data-testid={`completed-invoices-body-${testId}`}>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search invoice / client / customer"
                     className="h-7 pl-7 text-xs bg-white"
                     data-testid={`completed-invoices-search-${testId}`} />
            </div>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                    onClick={fetchList} title="Refresh list"
                    data-testid={`completed-invoices-refresh-${testId}`}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            {/* Mobile-only scope switcher (header one is hidden < sm) */}
            <span className="sm:hidden inline-flex items-center rounded-full bg-white border border-emerald-200 overflow-hidden"
                  data-testid={`completed-invoices-scope-mobile-${testId}`}>
              {SCOPE_OPTIONS.map((o) => (
                <button key={o.id} type="button"
                        onClick={() => setScopePersist(o.id)}
                        data-testid={`completed-invoices-scope-mobile-${o.id}-${testId}`}
                        className={`px-2 py-0.5 text-[10px] font-medium ${
                          scope === o.id
                            ? "bg-emerald-600 text-white"
                            : "text-emerald-700"
                        }`}>
                  {o.label}
                </button>
              ))}
            </span>
            {pendingOnly && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold"
                    data-testid={`completed-invoices-pending-chip-${testId}`}>
                Pending for this tool · {filtered.length}
                <button type="button"
                        onClick={() => setPendingOnly(false)}
                        className="ml-0.5 w-3.5 h-3.5 inline-flex items-center justify-center rounded-full hover:bg-amber-200"
                        data-testid={`completed-invoices-pending-chip-clear-${testId}`}
                        title="Clear pending-only filter">
                  ×
                </button>
              </span>
            )}
            {/* Iter 286 — usage colour legend */}
            <span className="hidden lg:inline-flex items-center gap-1.5 text-[9px] text-slate-400 ml-auto"
                  title="Chip colour = how many times the invoice was used in THIS tool"
                  data-testid={`use-color-legend-${testId}`}>
              <i className="w-2.5 h-2.5 rounded-sm bg-emerald-100 border border-emerald-300 inline-block" />×1
              <i className="w-2.5 h-2.5 rounded-sm bg-sky-100 border border-sky-300 inline-block" />×2
              <i className="w-2.5 h-2.5 rounded-sm bg-pink-100 border border-pink-300 inline-block" />×3
              <i className="w-2.5 h-2.5 rounded-sm bg-orange-100 border border-orange-300 inline-block" />×4
              <i className="w-2.5 h-2.5 rounded-sm bg-orange-200 border border-orange-400 inline-block" />×5+
            </span>
          </div>
          {filtered.length === 0 ? (
            <p className="text-[11px] text-slate-500 italic"
               data-testid={`completed-invoices-empty-${testId}`}>
              {useList.length === 0
                ? "No invoices marked Completed today yet."
                : "No invoices match the search."}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {filtered.map(inv => {
                // Iter 286 — usage colour coding (per tool): ×1 light green,
                // ×2 light blue, ×3 light pink, ×4 light purple, ×5+ deeper
                // purple. 0 uses = neutral white.
                const usedN = ((inv.uses_tool || {})[tool]) || 0;
                const tone = usedN === 0 ? "bg-white border-emerald-300"
                  : usedN === 1 ? "bg-emerald-100 border-emerald-300"
                  : usedN === 2 ? "bg-sky-100 border-sky-300"
                  : usedN === 3 ? "bg-pink-100 border-pink-300"
                  : usedN === 4 ? "bg-orange-100 border-orange-300"
                  : "bg-orange-200 border-orange-400";
                return (
                <span key={inv.invoice_key}
                      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] ${tone}`}
                      title={usedN ? `Used ${usedN}× in ${toolLabel(tool)} (chip colour shows usage)` : "Not used in this tool yet"}
                      data-testid={`completed-invoice-chip-${testId}-${inv.invoice_key}`}>
                  <span className="font-mono font-bold text-emerald-800">{inv.invoice_no}</span>
                  <InTableBadge inv={inv} history={history} tool={tool} testId={testId} />
                  <ReadyBadge invoiceNo={inv.invoice_no || inv.invoice_key} testId={`chip-${testId}`} />
                  {inv.label_pending && (
                    <span className="text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-300 rounded-full px-1.5 cursor-default animate-pulse"
                          title="Bulk data exists for this invoice but no labels were generated yet — click Use to generate labels"
                          data-testid={`label-pending-${testId}-${inv.invoice_key}`}>
                      label pending
                    </span>
                  )}
                  {(inv.codes || []).length > 0 && (
                    <span className="text-slate-500">{inv.codes.join("/")}</span>
                  )}
                  <span className="text-slate-500">· {inv.parcel_count - (inv.cancelled_parcels || 0)} parcel{inv.parcel_count - (inv.cancelled_parcels || 0) === 1 ? "" : "s"}</span>
                  {inv.cancelled_parcels > 0 && (
                    <span className="text-[9px] font-bold bg-rose-100 text-rose-700 border border-rose-300 rounded-full px-1.5"
                          title={`${inv.cancelled_parcels} parcel(s) cancelled in Order Register — left out of new documents`}
                          data-testid={`cancelled-count-${testId}-${inv.invoice_key}`}>−{inv.cancelled_parcels} cancelled</span>
                  )}
                  {(() => {
                    // Iter 228 — "used ×N" badge: how many times this tool
                    // has generated output from the invoice, split per flow.
                    const used = usedN;
                    if (!used) return null;
                    const flows = Object.entries(inv.uses_flows || {})
                      .filter(([k]) => k.startsWith(`${tool}:`))
                      .map(([k, v]) => `${k.split(":")[1] === "noslip" ? "without slip" : k.split(":")[1] === "slip" ? "with slip" : k.split(":")[1]} ×${v}`)
                      .join(", ");
                    const last = inv.uses_last_at
                      ? `Last: ${new Date(inv.uses_last_at).toLocaleString()} by ${inv.uses_last_by || "?"}`
                      : "";
                    return (
                      <span className="text-[9px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-full px-1.5 cursor-default"
                            title={`Used in ${toolLabel(tool)}: ${used}× (${flows})${last ? "\n" + last : ""}`}
                            data-testid={`use-count-${testId}-${inv.invoice_key}`}>
                        ×{used}
                      </span>
                    );
                  })()}
                  <Button size="sm"
                          className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
                          disabled={busy}
                          onClick={() => onUse(inv)}
                          title={`Generate from invoice ${inv.invoice_no} — no order sheet upload needed`}
                          data-testid={`use-invoice-${testId}-${inv.invoice_key}`}>
                    Use
                  </Button>
                </span>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
    <DirectCreateBox invoices={directList} tool={tool} testId={testId}
                     busy={busy} onUse={onUse} scopeLabel={scopeLabel} history={history} />
    </div>
  );
};

export default CompletedInvoicesBar;
